import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { OpenInDriveButton } from '@/components/drive/OpenInDriveButton';
import { requestDriveRenameCheck } from '@/components/drive/DriveRenamePrompt';
import { ArrowLeft, MapPin, User, Home, Save, Plus, Trash2, Edit2, Check, X, ClipboardCheck, Building2 } from 'lucide-react';
import RoofForm from '@/components/RoofForm';
import Combobox from '@/components/Combobox';
import MultiSelectChips from '@/components/MultiSelectChips';
import AgentFormModal from '@/components/AgentFormModal';
import { AppraisalStartButtons } from '@/components/AppraisalStartButtons';
import RoofDiagramPickerInline from '@/components/RoofDiagramPickerInline';
import AttachmentsTab from '@/components/AttachmentsTab';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';
import PropertyStatusCard from '@/components/PropertyStatusCard';
import IdChip from '@/components/IdChip';
import { fireRoofDetailsChanged } from '@/utils/roofEvents';

export default function PropertyDetailPage() {
  const { api } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Iter303aa — pane filter. `?pane=property_details|tenant|agent|roof_details`
  // hides all sections except the named one, so the page can be opened inside
  // a Workflow Runner pop-out that shows just the sub-form the operator
  // needs. Empty pane = show everything (default behaviour).
  const pane = searchParams.get('pane') || '';
  const paneFilter = !!pane;
  const showSection = (name) => !paneFilter || pane === name;
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newRoof, setNewRoof] = useState(null);
  const [editRoof, setEditRoof] = useState(null);
  const [cityOptions, setCityOptions] = useState([]);
  const [suburbOptions, setSuburbOptions] = useState([]);
  const [suburbMeta, setSuburbMeta] = useState([]);
  const [propertyCommentOptions, setPropertyCommentOptions] = useState([]);
  const [tenantCommentOptions, setTenantCommentOptions] = useState([]);
  const [preferredWorkTimes, setPreferredWorkTimes] = useState([]);
  const [preferredContactMethods, setPreferredContactMethods] = useState([]);
  const [agents, setAgents] = useState([]);
  // Iter194 — full client list for the Transfer-of-Ownership modal
  // dropdown. Lazy-loaded the first time the operator opens the card.
  const [allClients, setAllClients] = useState([]);
  useEffect(() => {
    let stopped = false;
    api.get('/clients?limit=500').then(({ data }) => {
      if (stopped) return;
      const items = Array.isArray(data) ? data : (data?.items || []);
      setAllClients(items);
    }).catch(() => { /* ignore */ });
    return () => { stopped = true; };
  }, [api]);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [editAgent, setEditAgent] = useState(null);

  // Refs let the `?edit=section` deep-link scroll the user straight to
  // the relevant card on load (e.g. landing from the Client page's
  // "Tenant" pencil should pop you down to the rental section ready to
  // edit, not at the top of the page).
  const propertyDetailsRef = useRef(null);
  const rentalRef = useRef(null);
  const agentRef = useRef(null);

  const loadAgents = useCallback(async () => {
    try {
      const { data } = await api.get('/agents');
      setAgents(Array.isArray(data) ? data : []);
    } catch { setAgents([]); }
  }, [api]);

  useEffect(() => {
    if (!editing) return;
    Promise.all([
      api.get('/dropdowns/address_cities').catch(() => ({ data: [] })),
      api.get('/dropdowns/address_suburbs').catch(() => ({ data: [] })),
      api.get('/dropdowns/address_suburbs_meta').catch(() => ({ data: [] })),
      api.get('/dropdowns/property_comments_options').catch(() => ({ data: [] })),
      api.get('/dropdowns/tenant_comments_options').catch(() => ({ data: [] })),
      api.get('/dropdowns/preferred_work_times').catch(() => ({ data: [] })),
      api.get('/dropdowns/preferred_contact_methods').catch(() => ({ data: [] })),
    ]).then(([c, s, sm, pc, tc, pwt, pcm]) => {
      setCityOptions(Array.isArray(c.data) ? c.data : []);
      setSuburbOptions(Array.isArray(s.data) ? s.data : []);
      setSuburbMeta(Array.isArray(sm.data) ? sm.data : []);
      setPropertyCommentOptions(Array.isArray(pc.data) ? pc.data : []);
      setTenantCommentOptions(Array.isArray(tc.data) ? tc.data : []);
      setPreferredWorkTimes(Array.isArray(pwt.data) ? pwt.data : []);
      setPreferredContactMethods(Array.isArray(pcm.data) ? pcm.data : []);
    });
    loadAgents();
  }, [api, editing, loadAgents]);

  const load = useCallback(async () => {
    try {
      const { data: prop } = await api.get(`/properties/${id}`);
      setData(prop);
      setForm(prop);
    } catch { navigate('/properties'); }
    setLoading(false);
  }, [api, id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Deep-link handler: `?edit=property|tenant|agent` lands the user in
  // edit mode and scrolls to the relevant card. For `tenant` we also
  // pre-tick the rental toggle so the tenant fields appear immediately.
  // Runs once per (data, edit param) — clears the query param after
  // activating so a browser refresh doesn't re-trigger the scroll.
  useEffect(() => {
    if (!data) return;
    const target = searchParams.get('edit');
    if (!target) return;
    setEditing(true);
    if (target === 'tenant' && !data.is_rental) {
      setForm(f => ({ ...f, is_rental: true }));
    }
    const ref = target === 'tenant' ? rentalRef : target === 'agent' ? agentRef : propertyDetailsRef;
    // Small timeout so the section has rendered after setEditing flip.
    setTimeout(() => { ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 60);
    // Only remove the `edit` param; preserve `pane` and anything else so
    // the pane filter (opened from the Workflow Runner) stays in effect
    // for the rest of the pop-out session.
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('edit');
      return next;
    }, { replace: true });
  }, [data, searchParams, setSearchParams]);

  const renderAppraisalButtons = (compact, prefix) => (
    <AppraisalStartButtons api={api} clientId={data.client_id} properties={[data]} fixedPropertyId={id}
      roofTypes={propertyRoofTypes} preferred={data.preferred_quote_types || []} compact={compact} testIdPrefix={prefix} />
  );


  const saveProperty = async () => {
    try {
      await api.put(`/properties/${id}`, form);
    } catch (e) {
      if (e?.response?.data?.code !== 'duplicate_address') alert(e?.response?.data?.detail || 'Failed to save property');
      return;
    }
    setEditing(false);
    load();
    requestDriveRenameCheck({ property_id: id });
  };

  // Iter303ag-fix5 — Live-save the multi-select `preferred_quote_types`
  // list from the "Preferred Quote Selection" sidebar card. Toggles a
  // single value in/out of the list. Optimistically updates local state
  // then PATCHes the backend. Rolls back on error. The singular
  // `preferred_quote_type` (the "primary" appraisal type) is NOT touched
  // by this card — that's set explicitly later in the Appraisal Booked
  // status flow.
  const togglePreferredQuoteType = async (value) => {
    const cur = Array.isArray(data?.preferred_quote_types) ? data.preferred_quote_types : [];
    const next = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value];
    setData(d => ({ ...d, preferred_quote_types: next }));
    setForm(f => ({ ...f, preferred_quote_types: next }));
    try {
      await api.patch(`/properties/${id}`, { preferred_quote_types: next });
    } catch (e) {
      // Roll back on failure so the UI stays truthful.
      setData(d => ({ ...d, preferred_quote_types: cur }));
      setForm(f => ({ ...f, preferred_quote_types: cur }));
    }
  };

  const clearPreferredQuoteTypes = async () => {
    const cur = Array.isArray(data?.preferred_quote_types) ? data.preferred_quote_types : [];
    setData(d => ({ ...d, preferred_quote_types: [] }));
    setForm(f => ({ ...f, preferred_quote_types: [] }));
    try {
      await api.patch(`/properties/${id}`, { preferred_quote_types: [] });
    } catch (e) {
      setData(d => ({ ...d, preferred_quote_types: cur }));
      setForm(f => ({ ...f, preferred_quote_types: cur }));
    }
  };

  const [deletingApp, setDeletingApp] = useState(null);
  // Iter178 — universal Soft/Hard Delete dialog target. Shape:
  // { kind: 'property'|'appraisal', id, label } or null.
  const [delTarget, setDelTarget] = useState(null);

  const deleteAppraisal = async (appId) => {
    // Iter177 — soft-delete fallthrough preserved for legacy callers.
    try {
      await api.delete(`/appraisals/${appId}?mode=soft`);
      setDeletingApp(null);
      load();
    } catch (e) { alert('Failed to delete appraisal'); }
  };

  const addRoofDetail = async () => {
    if (!newRoof) return;
    await api.post('/roof-details', { ...newRoof, property_id: id });
    setNewRoof(null);
    load();
    fireRoofDetailsChanged();
  };

  const updateRoofDetail = async () => {
    if (!editRoof?._id) return;
    const { _id, ...rest } = editRoof;
    await api.put(`/roof-details/${_id}`, { ...rest, property_id: id });
    setEditRoof(null);
    load();
    fireRoofDetailsChanged();
  };

  const deleteRoofDetail = async (roofId) => {
    if (!window.confirm('Delete this roof detail?')) return;
    await api.delete(`/roof-details/${roofId}`);
    load();
    fireRoofDetailsChanged();
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">Loading...</div>;
  if (!data) return null;

  const client = data.client;
  const roofs = data.roof_details || [];
  // Iter207 — collect the distinct roof types recorded on the property
  // so the "+ New Appraisal" picker can shortlist quote_types that
  // actually apply to this roof (e.g. don't surface Concrete Tile
  // workflows when the property is Long Run Iron).
  const propertyRoofTypes = Array.from(new Set(
    (roofs || []).map(r => (r?.roof_type || '').trim()).filter(Boolean)
  ));

  return (
    <div data-testid="property-detail-page" className="space-y-5">
      {!paneFilter && (
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#D35400] transition-colors font-medium">
          <ArrowLeft size={16} /> Back
        </button>
      )}
      {!paneFilter && (
      <div className="bg-white border border-[#E2E2DF] border-t-[3px] border-t-[#D35400] rounded-md p-6 relative">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#D35400]/15 to-[#D35400]/5 flex items-center justify-center flex-shrink-0 ring-1 ring-[#D35400]/10">
              <Home size={22} className="text-[#D35400]" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <p className="eyebrow mb-0.5 inline-flex items-center gap-1.5">Property <IdChip type="property" value={data.property_no || data.access_id || ''} size="md" testId="property-header-id-chip" /></p>
              <h1 className="text-2xl font-extrabold text-slate-900 font-[Manrope] tracking-tight truncate">
                {data.property_address_num} {data.property_address_street}
              </h1>
              <div className="flex gap-2 mt-2"><OpenInDriveButton entity="property" id={id} />
              <a data-testid="property-update-resend-btn" href={`/update-resend?property=${id}`} className="inline-flex items-center text-xs px-3 py-1.5 rounded-md border border-[#D35400]/40 text-[#D35400] font-semibold hover:bg-orange-50">Update & Resend</a></div>
              <p className="text-sm text-slate-500 mt-1">{data.property_address_suburb}{data.property_address_city ? `, ${data.property_address_city}` : ''}{data.property_area_code ? ` · ${data.property_area_code}` : ''}</p>
              {/* Property-type chip + edit picker. Distinguishes the
                  client's own home from rentals / investments / holiday
                  homes / commercial premises. When set to "Rental", the
                  legacy is_rental flag auto-flips so existing filters
                  keep working. */}
              {editing ? (
                <select
                  data-testid="property-type-select"
                  value={form.property_type || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm({
                      ...form,
                      property_type: v,
                      ...(v === 'Rental' ? { is_rental: true } : {}),
                    });
                  }}
                  className="mt-2 text-xs border border-slate-300 rounded-md px-2 py-1"
                >
                  <option value="">— Property type —</option>
                  <option value="Owner-Occupied">Owner-Occupied (Client&apos;s home)</option>
                  <option value="Rental">Rental</option>
                  <option value="Investment">Investment</option>
                  <option value="Holiday Home">Holiday Home</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Mixed Use">Mixed Use</option>
                </select>
              ) : data.property_type ? (
                <span
                  data-testid="property-type-chip"
                  className={`mt-2 inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ring-1 ${
                    data.property_type === 'Owner-Occupied' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' :
                    data.property_type === 'Rental'         ? 'bg-orange-50 text-orange-700 ring-orange-200' :
                    data.property_type === 'Investment'     ? 'bg-violet-50 text-violet-700 ring-violet-200' :
                    data.property_type === 'Holiday Home'   ? 'bg-cyan-50 text-cyan-700 ring-cyan-200' :
                    data.property_type === 'Commercial'     ? 'bg-slate-100 text-slate-700 ring-slate-200' :
                                                              'bg-amber-50 text-amber-700 ring-amber-200'
                  }`}
                >
                  {data.property_type}
                </span>
              ) : null}

              {/* Iter208 — Preferred quote type tag picker. Single-select
                  pill buttons. Drives the NewAppraisalKindButton's
                  cascade to pin matching quote_types to the very top
                  of the "Recommended for this roof" list with a ★
                  star. */}
              {editing ? (
                <PreferredQuoteTypePills
                  value={form.preferred_quote_type || ''}
                  onChange={(v) => setForm({ ...form, preferred_quote_type: v })}
                />
              ) : data.preferred_quote_type ? (
                <div className="mt-2">
                  <span
                    data-testid="preferred-quote-type-chip"
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ring-1 bg-sky-50 text-sky-700 ring-sky-200"
                  >
                    ★ Prefers: {data.preferred_quote_type}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {!editing ? (
              <>
                {renderAppraisalButtons(false, 'property')}
                <button onClick={() => setEditing(true)} className="flex items-center gap-2 px-3.5 py-2 bg-[#D35400] text-white rounded-md text-sm font-semibold hover:bg-[#A04000] shadow-sm hover:shadow-md hover:shadow-orange-500/20 transition-all"><Edit2 size={14} strokeWidth={2.5} /> Edit</button>
                {/* Iter178 — Delete Property (soft / hard with
                    safety guards against active appraisals). */}
                <button
                  data-testid="delete-property-btn"
                  onClick={() => setDelTarget({
                    kind: 'property',
                    id,
                    label: `${data.property_address_num || ''} ${data.property_address_street || ''}`.trim() || `Property ${data.property_no || id.slice(-6)}`,
                  })}
                  className="flex items-center gap-1.5 px-3.5 py-2 border border-rose-300 text-rose-600 rounded-md text-sm font-semibold hover:bg-rose-50 transition-colors"
                  title="Delete this property (soft or hard, with dependent guard)"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </>
            ) : (
              <>
                <button onClick={saveProperty} data-testid="property-save-btn" className="flex items-center gap-1 px-3.5 py-2 bg-emerald-600 text-white rounded-md text-sm font-semibold hover:bg-emerald-700"><Save size={14} /> Save</button>
                <button onClick={() => { setEditing(false); setForm(data); }} data-testid="property-cancel-btn" className="px-3.5 py-2 border border-[#E2E2DF] rounded-md text-sm hover:bg-slate-50 font-medium">Cancel</button>
              </>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Iter303ad — Floating Save/Cancel bar shown in the runner pop-out
          (paneFilter mode). Header is hidden in pane mode; this thin bar
          becomes the primary save affordance so the operator doesn't have
          to hunt for it. */}
      {paneFilter && (
        <div data-testid="pane-save-bar" className="sticky top-0 z-10 bg-white border border-[#E2E2DF] rounded-md px-4 py-2 flex items-center gap-2 shadow-sm">
          <div className="text-xs uppercase tracking-widest text-[#D35400] font-semibold">Editing property details</div>
          <span className="flex-1" />
          {!editing && (
            <button onClick={() => setEditing(true)} data-testid="pane-edit-btn"
              className="px-3 py-1.5 border border-[#D35400] text-[#D35400] rounded text-xs font-semibold hover:bg-[#FFF7ED]">
              <Edit2 size={12} className="inline mr-1" /> Edit
            </button>
          )}
          {editing && (
            <>
              <button onClick={saveProperty} data-testid="pane-save-btn"
                className="px-3.5 py-1.5 bg-emerald-600 text-white rounded text-xs font-semibold hover:bg-emerald-700 flex items-center gap-1">
                <Save size={12} /> Save
              </button>
              <button onClick={() => { setEditing(false); setForm(data); }} data-testid="pane-cancel-btn"
                className="px-3.5 py-1.5 border border-[#E2E2DF] rounded text-xs hover:bg-slate-50 font-medium">
                Cancel
              </button>
            </>
          )}
          <button onClick={() => window.close()} data-testid="pane-close-btn"
            className="px-2 py-1.5 text-slate-400 hover:text-slate-700 text-xs" title="Close window">✕</button>
        </div>
      )}

      {/* Client Link */}
      {client && showSection('client_link') && (
        <div className="flex items-center gap-3 bg-blue-50/60 border border-blue-100 rounded-md px-4 py-3 cursor-pointer hover:bg-blue-50 hover:border-blue-200 transition-all group" onClick={() => navigate(`/clients/${client._id}`)}>
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center ring-1 ring-blue-200">
            <User size={14} className="text-blue-700" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-blue-900">{client.name_first} {client.name_last}</p>
            <p className="text-xs text-blue-600/80 truncate">{[client.email, client.phone_mobile].filter(Boolean).join(' · ')}</p>
          </div>          <span className="text-xs text-blue-600 font-semibold opacity-0 group-hover:opacity-100 transition-opacity">View client →</span>
        </div>
      )}

      {/* Iter194 — Phase C: Status + Ownership controls */}
      {showSection('status_card') && (
        <PropertyStatusCard
          propertyId={id}
          data={data}
          clients={allClients}
          onUpdated={(updated) => setData(prev => ({ ...prev, ...updated }))}
        />
      )}

      {showSection('roof_diagram') && (
        <RoofDiagramPickerInline api={api} propertyId={id} onChanged={load} />
      )}

      {/* Iter206 — 2-col split: existing Property/Tenant/Agent cards
          collapse to ~2/3 width on lg+ screens, leaving a sticky
          Appraisals sidebar on the right for at-a-glance status +
          one-click "New appraisal/quote" creation. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        <div className="lg:col-span-2 space-y-5">
      {/* Property Details */}
      {showSection('property_details') && (
      <div ref={propertyDetailsRef} className="bg-white border border-[#E2E2DF] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-[#2C2C2C] flex items-center gap-2"><MapPin size={16} className="text-[#D35400]" /> Property Details</h2>
          {!editing && (
            <button
              data-testid="edit-property-details-btn"
              onClick={() => setEditing(true)}
              className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#D35400] border border-[#D35400] rounded-md hover:bg-[#FFF7ED]"
            >
              <Edit2 size={12} strokeWidth={2.5} /> Edit details
            </button>
          )}
        </div>
        {/* Iter303ag-fix5 — Read-only chip strip showing the multi-select
            "Preferred Work Types" captured at intake (Action 5). Pure
            reference; primary appraisal type is decided later in the
            Appraisal Booked workflow. Editable via the sidebar
            "Preferred Quote Selection" card. */}
        {Array.isArray(data.preferred_quote_types) && data.preferred_quote_types.length > 0 && (
          <div className="mb-4 flex items-center flex-wrap gap-1.5" data-testid="preferred-work-types-chips">
            <span className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-500 mr-1">Preferred Work Types:</span>
            {data.preferred_quote_types.map(v => (
              <span key={v} className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-semibold">
                {v}
              </span>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { key: 'property_address_num', label: 'Street Number' },
            { key: 'property_address_street', label: 'Street Name' },
            { key: 'property_address_suburb', label: 'Suburb' },
            { key: 'property_address_city', label: 'City' },
            { key: 'property_area_code', label: 'Area Code' },
            { key: 'map_reference', label: 'Map Reference' },
          ].map(f => {
            const isSuburb = f.key === 'property_address_suburb';
            const isCity = f.key === 'property_address_city';
            return (
              <div key={f.key}>
                <label className="text-xs font-medium text-slate-500 block mb-1">{f.label}</label>
                {editing ? (
                  isCity ? (
                    <Combobox
                      value={form.property_address_city || ''}
                      onChange={v => setForm(s => ({ ...s, property_address_city: v }))}
                      options={cityOptions}
                      placeholder="Hamilton…"
                      testId="property-city-combobox"
                    />
                  ) : isSuburb ? (
                    <Combobox
                      value={form.property_address_suburb || ''}
                      onChange={v => setForm(s => {
                        const next = { ...s, property_address_suburb: v };
                        const meta = suburbMeta.find(m => m.name === v);
                        if (meta?.city && !s.property_address_city) next.property_address_city = meta.city;
                        return next;
                      })}
                      options={
                        form.property_address_city
                          ? [
                              ...suburbMeta.filter(m => m.city === form.property_address_city).map(m => m.name),
                              ...suburbOptions.filter(s => !suburbMeta.find(m => m.name === s && m.city === form.property_address_city)),
                            ]
                          : suburbOptions
                      }
                      placeholder="Dinsdale…"
                      testId="property-suburb-combobox"
                      hint={(opt) => suburbMeta.find(x => x.name === opt)?.city || null}
                    />
                  ) : (
                    <input value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" />
                  )
                ) : (
                  <p className="text-sm text-slate-800">{data[f.key] || '-'}</p>
                )}
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-slate-500 block mb-1">Property Access Flags</label>
            {editing ? (
              <MultiSelectChips
                testId="property-comments-multi"
                value={form.property_comments_list || []}
                onChange={list => setForm({ ...form, property_comments_list: list })}
                options={propertyCommentOptions}
                placeholder="Pick flags — dog, locked gate, alarm…"
                onAddNew={async (v) => { await api.post('/dropdowns/property_comments_options/add', { value: v }); setPropertyCommentOptions(o => [...o, v]); }}
              />
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {(data.property_comments_list && data.property_comments_list.length > 0)
                  ? data.property_comments_list.map(c => <span key={c} className="text-xs px-2.5 py-0.5 rounded-full bg-[#FFF7ED] text-[#A04000] border border-[#D35400]/30 font-semibold">{c}</span>)
                  : <span className="text-sm text-slate-400">—</span>}
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-slate-500 block mb-1">Additional Notes</label>
            {editing
              ? <textarea data-testid="property-comments" value={form.property_comments || ''} onChange={e => setForm({ ...form, property_comments: e.target.value })} placeholder="Anything not captured by the flags above (combination codes, side gate quirks, etc.)" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" rows={2} />
              : <p className="text-sm text-slate-800 whitespace-pre-wrap">{data.property_comments || '-'}</p>}
          </div>
        </div>
      </div>
      )}

      {/* Rental / Tenant section */}
      {showSection('tenant') && (
      <div ref={rentalRef} className="bg-white border border-[#E2E2DF] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-[#2C2C2C] flex items-center gap-2">
            <User size={16} className="text-[#D35400]" /> Rental / Tenant Information
          </h2>
          {editing && (
            <label data-testid="is-rental-toggle" className="flex items-center gap-2 cursor-pointer text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
              <input type="checkbox" checked={!!form.is_rental} onChange={e => setForm({ ...form, is_rental: e.target.checked })} className="accent-[#D35400] w-4 h-4" />
              This property is a rental
            </label>
          )}
          {!editing && data.is_rental && <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#D35400] bg-[#FFF7ED] border border-[#D35400]/30 rounded-full px-2.5 py-0.5">Rental</span>}
          {!editing && (
            <button
              data-testid="edit-tenant-info-btn"
              onClick={() => {
                // One-click affordance: drop the page into edit mode AND, when
                // the property isn't yet flagged rental, pre-tick the toggle
                // so the tenant fields appear immediately. Saves the user a
                // second click + scroll.
                if (!data.is_rental) setForm(s => ({ ...s, is_rental: true }));
                setEditing(true);
              }}
              className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#D35400] border border-[#D35400] rounded-md hover:bg-[#FFF7ED] ml-auto"
            >
              <Edit2 size={12} strokeWidth={2.5} /> {data.is_rental ? 'Edit tenant info' : 'Add tenant info'}
            </button>
          )}
        </div>

        {(editing && form.is_rental) || (!editing && (data.is_rental || data.tenant_contact_name)) ? (
          <div className="space-y-4">
            {/* Tenant identity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Tenant Name</label>
                {editing ? <input data-testid="tenant-name" value={form.tenant_contact_name || ''} onChange={e => setForm({ ...form, tenant_contact_name: e.target.value })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" /> : <p className="text-sm text-slate-800">{data.tenant_contact_name || '-'}</p>}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Tenant Phone</label>
                {editing ? <input data-testid="tenant-phone" value={form.tenant_contact_number || ''} onChange={e => setForm({ ...form, tenant_contact_number: e.target.value })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" /> : <p className="text-sm text-slate-800">{data.tenant_contact_number || '-'}</p>}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Tenant Email</label>
                {editing ? <input data-testid="tenant-email" type="email" value={form.tenant_email || ''} onChange={e => setForm({ ...form, tenant_email: e.target.value })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" /> : <p className="text-sm text-slate-800">{data.tenant_email || '-'}</p>}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Preferred Contact Method</label>
                {editing ? (
                  <div className="flex flex-wrap gap-2">
                    {(preferredContactMethods.length ? preferredContactMethods : ['Text', 'Email', 'Phone']).map(m => (
                      <button key={m} data-testid={`pref-contact-${m.toLowerCase()}`} type="button" onClick={() => setForm({ ...form, tenant_preferred_contact_method: m })} className={`px-3 py-1.5 text-sm rounded-md border ${ (form.tenant_preferred_contact_method || 'Text') === m ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white border-[#E2E2DF] text-slate-600 hover:border-[#D35400]'}`}>{m}</button>
                    ))}
                  </div>
                ) : <p className="text-sm text-slate-800">{data.tenant_preferred_contact_method || 'Text'}</p>}
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-slate-500 block mb-1">Preferred Work Time</label>
                {editing ? (
                  <Combobox
                    testId="tenant-preferred-time-combobox"
                    value={form.tenant_preferred_work_time || ''}
                    onChange={v => setForm({ ...form, tenant_preferred_work_time: v })}
                    options={preferredWorkTimes}
                    placeholder="e.g. After 4pm weekdays · Weekends only"
                    onAddNew={async (v) => { await api.post('/dropdowns/preferred_work_times/add', { value: v }); setPreferredWorkTimes(o => [...o, v]); }}
                  />
                ) : <p className="text-sm text-slate-800">{data.tenant_preferred_work_time || '-'}</p>}
              </div>
            </div>

            {/* Special requirements toggle + structured options */}
            <div className="border-t border-[#E2E2DF] pt-4">
              {editing ? (
                <label data-testid="special-req-toggle" className="flex items-center gap-2 cursor-pointer text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">
                  <input type="checkbox" checked={!!form.tenant_has_special_requirements} onChange={e => setForm({ ...form, tenant_has_special_requirements: e.target.checked })} className="accent-[#D35400] w-4 h-4" />
                  Tenant has special requirements
                </label>
              ) : (data.tenant_has_special_requirements && <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#D35400] mb-3">Special requirements:</p>)}

              {(editing && form.tenant_has_special_requirements) || (!editing && data.tenant_has_special_requirements) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Dogs */}
                  <div className="bg-slate-50 border border-[#E2E2DF] rounded-md p-3">
                    <label className="flex items-center justify-between text-xs font-medium text-slate-600">
                      <span>Dogs on property</span>
                      {editing
                        ? <input data-testid="tenant-dogs" type="number" min="0" max="20" value={form.tenant_dogs ?? 0} onChange={e => setForm({ ...form, tenant_dogs: parseInt(e.target.value) || 0 })} className="w-16 px-2 py-1 border border-[#E2E2DF] rounded text-sm font-mono-num text-center" />
                        : <span className="font-mono-num font-semibold text-slate-800">{data.tenant_dogs || 0}</span>}
                    </label>
                  </div>
                  {/* Gate */}
                  <div className="bg-slate-50 border border-[#E2E2DF] rounded-md p-3">
                    {editing ? (
                      <>
                        <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-600 mb-1">
                          <input data-testid="gate-locked" type="checkbox" checked={!!form.tenant_gate_locked} onChange={e => setForm({ ...form, tenant_gate_locked: e.target.checked })} className="accent-[#D35400] w-3.5 h-3.5" />
                          Locked gate
                        </label>
                        {form.tenant_gate_locked && (
                          <input data-testid="gate-code" value={form.tenant_gate_code || ''} onChange={e => setForm({ ...form, tenant_gate_code: e.target.value })} placeholder="Code or key location" className="w-full px-2 py-1 border border-[#E2E2DF] rounded text-xs" />
                        )}
                      </>
                    ) : data.tenant_gate_locked ? (
                      <div><span className="text-xs font-medium text-slate-600">Locked gate: </span><span className="font-semibold text-slate-800">{data.tenant_gate_code || '(no code recorded)'}</span></div>
                    ) : null}
                  </div>
                  {/* Shift worker */}
                  <div className="md:col-span-2 bg-slate-50 border border-[#E2E2DF] rounded-md p-3">
                    {editing ? (
                      <>
                        <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-600 mb-1">
                          <input data-testid="shift-worker" type="checkbox" checked={!!form.tenant_is_shift_worker} onChange={e => setForm({ ...form, tenant_is_shift_worker: e.target.checked })} className="accent-[#D35400] w-3.5 h-3.5" />
                          Shift worker
                        </label>
                        {form.tenant_is_shift_worker && (
                          <input data-testid="shift-info" value={form.tenant_shift_info || ''} onChange={e => setForm({ ...form, tenant_shift_info: e.target.value })} placeholder="e.g. Nights 9pm-6am — quiet 9am-3pm" className="w-full px-2 py-1 border border-[#E2E2DF] rounded text-xs" />
                        )}
                      </>
                    ) : data.tenant_is_shift_worker ? (
                      <div><span className="text-xs font-medium text-slate-600">Shift worker: </span><span className="font-semibold text-slate-800">{data.tenant_shift_info || '(no shift recorded)'}</span></div>
                    ) : null}
                  </div>
                  {/* Misc flags via multi-select chips */}
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium text-slate-500 block mb-1">Tenant Flags</label>
                    {editing ? (
                      <MultiSelectChips
                        testId="tenant-comments-multi"
                        value={form.tenant_comments_list || []}
                        onChange={list => setForm({ ...form, tenant_comments_list: list })}
                        options={tenantCommentOptions}
                        placeholder="Pick tenant flags — shift worker, deaf, elderly…"
                        onAddNew={async (v) => { await api.post('/dropdowns/tenant_comments_options/add', { value: v }); setTenantCommentOptions(o => [...o, v]); }}
                      />
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {(data.tenant_comments_list && data.tenant_comments_list.length > 0)
                          ? data.tenant_comments_list.map(c => <span key={c} className="text-xs px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-semibold">{c}</span>)
                          : <span className="text-sm text-slate-400">—</span>}
                      </div>
                    )}
                  </div>
                  {/* Free-text overflow */}
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium text-slate-500 block mb-1">Other notes</label>
                    {editing ? <textarea data-testid="tenant-comments" value={form.tenant_comments || ''} onChange={e => setForm({ ...form, tenant_comments: e.target.value })} placeholder="Anything else worth knowing…" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" rows={2} /> : <p className="text-sm text-slate-800 whitespace-pre-wrap">{data.tenant_comments || '-'}</p>}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic">Owner-occupied — no tenant information.{editing && ' Tick the rental toggle above to add tenant details.'}</p>
        )}
      </div>
      )}

      {/* Real Estate Agent (managing agent) — only when rental is on */}
      {showSection('agent') && ((editing && form.is_rental) || (!editing && (data.is_rental || data.real_estate_agent_id || data.agent_name))) && (
        <div ref={agentRef} className="bg-white border border-[#E2E2DF] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-base font-semibold text-[#2C2C2C] flex items-center gap-2">
              <Building2 size={16} className="text-[#D35400]" /> Managing Agent
            </h2>
            <div className="flex items-center gap-2">
              {editing && (
                <label data-testid="agent-same-as-owner" className="flex items-center gap-2 cursor-pointer text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                  <input
                    type="checkbox"
                    checked={!!form.agent_same_as_owner}
                    onChange={e => {
                      if (e.target.checked && client) {
                        setForm({
                          ...form,
                          agent_same_as_owner: true,
                          real_estate_agent_id: null,
                          agent_name: `${client.name_first || ''} ${client.name_last || ''}`.trim(),
                          agent_company: client.company_name || '',
                          agent_phone: client.phone_mobile || client.phone_work || '',
                          agent_email: client.email || '',
                        });
                      } else {
                        setForm({ ...form, agent_same_as_owner: false });
                      }
                    }}
                    className="accent-[#D35400] w-4 h-4"
                  />
                  Client is the agent (use owner details)
                </label>
              )}
              {!editing && (
                <button
                  data-testid="edit-agent-info-btn"
                  onClick={() => setEditing(true)}
                  className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#D35400] border border-[#D35400] rounded-md hover:bg-[#FFF7ED]"
                >
                  <Edit2 size={12} strokeWidth={2.5} /> Edit agent
                </button>
              )}
            </div>
          </div>

          {editing && !form.agent_same_as_owner && (
            <div className="mb-4 bg-slate-50 border border-[#E2E2DF] rounded-lg p-3">
              <label className="text-xs font-medium text-slate-600 block mb-1.5">Pick existing agent</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1">
                  <Combobox
                    testId="agent-picker-combobox"
                    value={(() => {
                      const a = agents.find(x => x._id === form.real_estate_agent_id);
                      return a ? `${a.agent_no} — ${a.name || ''} · ${a.company || ''}` : '';
                    })()}
                    onChange={(label) => {
                      // Map the visible label back to an agent _id
                      const a = agents.find(x => label && label.startsWith(x.agent_no));
                      if (a) {
                        setForm({
                          ...form,
                          real_estate_agent_id: a._id,
                          agent_name: a.name || '',
                          agent_company: a.company || '',
                          agent_phone: a.phone || '',
                          agent_email: a.email || '',
                          agent_same_as_owner: false,
                        });
                      } else if (!label) {
                        setForm({ ...form, real_estate_agent_id: null });
                      }
                    }}
                    options={agents.filter(a => a.active !== false).map(a => `${a.agent_no} — ${a.name || ''} · ${a.company || ''}`)}
                    placeholder="Search by company or name…"
                  />
                </div>
                <button
                  type="button"
                  data-testid="add-new-agent-btn"
                  onClick={() => { setEditAgent(null); setShowAgentModal(true); }}
                  className="flex items-center gap-1.5 px-3 py-2 bg-white border border-[#D35400] text-[#D35400] rounded-md text-sm font-semibold hover:bg-[#FFF7ED] whitespace-nowrap"
                >
                  <Plus size={14} strokeWidth={2.5} /> New Agent
                </button>
              </div>
              {form.real_estate_agent_id && (() => {
                const a = agents.find(x => x._id === form.real_estate_agent_id);
                if (!a) return null;
                return (
                  <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-slate-500">
                    <span>Linked to <span className="font-mono-num font-semibold text-[#D35400]">{a.agent_no}</span></span>
                    <button type="button" onClick={() => { setEditAgent(a); setShowAgentModal(true); }} className="underline hover:text-[#D35400]">Edit this agent</button>
                  </div>
                );
              })()}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Agent Name</label>
              {editing ? <input data-testid="agent-name" value={form.agent_name || ''} onChange={e => setForm({ ...form, agent_name: e.target.value, agent_same_as_owner: false })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" /> : <p className="text-sm text-slate-800">{data.agent?.name || data.agent_name || '-'}</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Agent Company</label>
              {editing ? <input data-testid="agent-company" value={form.agent_company || ''} onChange={e => setForm({ ...form, agent_company: e.target.value, agent_same_as_owner: false })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" placeholder="e.g. Waikato Real Estate" /> : <p className="text-sm text-slate-800">{data.agent?.company || data.agent_company || '-'}{data.agent?.agent_no && <span className="ml-2 text-[10px] font-mono-num text-[#D35400] font-bold">{data.agent.agent_no}</span>}</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Agent Phone</label>
              {editing ? <input data-testid="agent-phone" value={form.agent_phone || ''} onChange={e => setForm({ ...form, agent_phone: e.target.value, agent_same_as_owner: false })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" /> : <p className="text-sm text-slate-800">{data.agent?.phone || data.agent_phone || '-'}</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Agent Email</label>
              {editing ? <input data-testid="agent-email" type="email" value={form.agent_email || ''} onChange={e => setForm({ ...form, agent_email: e.target.value, agent_same_as_owner: false })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" /> : <p className="text-sm text-slate-800">{data.agent?.email || data.agent_email || '-'}</p>}
            </div>
          </div>

          <AgentFormModal
            open={showAgentModal}
            agent={editAgent}
            prefillFromClient={editAgent ? null : client}
            onClose={() => { setShowAgentModal(false); setEditAgent(null); }}
            onSaved={(saved) => {
              setShowAgentModal(false); setEditAgent(null);
              loadAgents();
              if (saved && saved._id) {
                setForm(s => ({
                  ...s,
                  real_estate_agent_id: saved._id,
                  agent_name: saved.name || '',
                  agent_company: saved.company || '',
                  agent_phone: saved.phone || '',
                  agent_email: saved.email || '',
                  agent_same_as_owner: false,
                }));
              }
            }}
          />
        </div>
      )}
        </div>{/* /lg:col-span-2 */}

        {/* Iter206 — Appraisals sidebar (sticky on large screens) */}
        {showSection('appraisals') && (
        <aside data-testid="property-appraisals-sidebar" className="lg:sticky lg:top-4 self-start space-y-3">
          {/* Iter211 — Preferred Quote Selection card. Sits ABOVE the
              Appraisals card, same column width, pill buttons (no
              dropdown). Live-saves to `preferred_quote_type` which the
              "+ New Appraisal" cascade reads to pin matching
              Quotation Types to the top of its shortlist. */}
          <PreferredQuoteSelectionCard
            values={Array.isArray(data.preferred_quote_types) ? data.preferred_quote_types : []}
            onToggle={togglePreferredQuoteType}
            onClear={clearPreferredQuoteTypes}
          />

          <div className="bg-white border border-[#E2E2DF] rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-[#2C2C2C] flex items-center gap-1.5">
                <ClipboardCheck size={14} className="text-[#D35400]" /> Appraisals
                {data.appraisals?.length > 0 && (
                  <span className="text-[10px] font-mono-num px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{data.appraisals.length}</span>
                )}
              </h2>
              <div className="flex gap-1.5">{renderAppraisalButtons(true, 'sidebar')}</div>
            </div>
            {(!data.appraisals || data.appraisals.length === 0) ? (
              <div className="text-center py-6 border border-dashed border-slate-200 rounded-md bg-slate-50/50">
                <ClipboardCheck size={20} className="text-slate-300 mx-auto" />
                <p className="text-xs text-slate-500 mt-2">No appraisals yet.</p>
                <p className="text-[10px] text-slate-400">Click <strong className="text-slate-600">+ New</strong> above to raise a free estimate, inspection or quote.</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {data.appraisals.map(a => (
                  <div
                    key={a._id}
                    data-testid={`sidebar-appraisal-${a._id}`}
                    onClick={() => navigate(`/appraisals/${a._id}`)}
                    className="flex items-start gap-2 p-2 border border-[#E2E2DF] rounded-md hover:border-[#D35400] hover:bg-[#FFF7ED]/40 cursor-pointer transition-colors group"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-semibold text-slate-800 truncate" title={a.quote_type_ref}>{a.quote_type_ref || 'Appraisal'}</span>
                        {a.appraisal_no && <IdChip type="appraisal" value={a.appraisal_no} size="sm" testId={`sidebar-appraisal-chip-${a._id}`} />}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{a.appraisal_status}</span>
                        {a.appraisal_date && <span className="text-[10px] text-slate-400">{String(a.appraisal_date).slice(0, 10)}</span>}
                      </div>
                      {a.contract_amount > 0 && (
                        <div className="text-[11px] font-mono-num font-bold text-emerald-700 mt-0.5">${Number(a.contract_amount).toLocaleString()}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        data-testid={`sidebar-edit-appraisal-${a._id}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/appraisals/${a._id}?edit=quote_type`); }}
                        title="Change quotation type"
                        className="p-0.5 text-slate-400 hover:text-[#D35400]"
                      ><Edit2 size={12} /></button>
                      <button
                        data-testid={`sidebar-delete-appraisal-${a._id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDelTarget({
                            kind: 'appraisal',
                            id: a._id,
                            label: a.appraisal_no ? `A${a.appraisal_no}` : `${a.quote_type_ref || 'Appraisal'} ${a._id.slice(-6)}`,
                          });
                        }}
                        className="p-0.5 text-slate-400 hover:text-rose-500"
                      ><Trash2 size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
        )}
      </div>{/* /grid */}

      {/* Roof Details */}
      {showSection('roof_details') && (
      <div className="bg-white border border-[#E2E2DF] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-[#2C2C2C] flex items-center gap-2"><Home size={16} className="text-[#D35400]" /> Roof Details ({roofs.length})</h2>
          <button onClick={() => setNewRoof({ description: 'House', roof_type: '', roof_colour: '', roof_age: '', roof_brand: '', roof_pitch: '', roof_profile: '', material_type: '', roof_stories: 1, colour_condition: '', organic_growth: '', roof_state: '', roof_m2: 0, floor_m2: 0, ridge_length: 0, roof_angle: 0, extra_work_comments: '', extra_work_estimate: 0 })} className="flex items-center gap-1 text-sm text-[#D35400] font-semibold hover:text-[#A04000]"><Plus size={14} /> Add Roof</button>
        </div>

        {newRoof && (
          <RoofForm roof={newRoof} setRoof={setNewRoof} onSave={addRoofDetail} onCancel={() => setNewRoof(null)} saveLabel="Save Roof" />
        )}

        {roofs.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">No roof details recorded</p>
        ) : (
          <div className="space-y-3">
            {roofs.map(r => editRoof?._id === r._id ? (
              <RoofForm key={r._id} roof={editRoof} setRoof={setEditRoof} onSave={updateRoofDetail} onCancel={() => setEditRoof(null)} saveLabel="Update Roof" />
            ) : (
              <div key={r._id} className="border border-[#E2E2DF] rounded-lg p-4 hover:border-slate-300 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-sm font-bold text-slate-800">{r.description || 'Main Roof'} <span className="text-slate-400 font-normal">·</span> <span className="text-slate-600 font-medium">{r.roof_type || 'Unknown Type'}</span></h3>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${r.roof_state === 'Good' || r.roof_state === 'Excellent' ? 'bg-green-100 text-green-700' : r.roof_state === 'Poor' || r.roof_state === 'Very Poor' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{r.roof_state || 'Not assessed'}</span>
                    <button data-testid={`edit-roof-${r._id}`} onClick={() => setEditRoof({...r})} className="p-1 text-slate-400 hover:text-[#D35400]"><Edit2 size={14} /></button>
                    <button data-testid={`delete-roof-${r._id}`} onClick={() => deleteRoofDetail(r._id)} className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 text-xs">
                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Description</div><div className="text-slate-800 font-medium">{r.description || '—'}</div></div>
                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Roof Type</div><div className="text-slate-800 font-medium">{r.roof_type || '—'}</div></div>
                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Brand</div><div className="text-slate-800 font-medium">{r.roof_brand || '—'}</div></div>
                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Profile</div><div className="text-slate-800 font-medium">{r.roof_profile || '—'}</div></div>
                  {/* Iter146 — Material Type surfaces here too. Always
                      rendered so the table layout stays consistent across
                      Iron and tile/concrete inspections; shows '—' for
                      non-Iron roofs where the field is left blank. The
                      small 'Iron' chip flags when this field is populated
                      because the roof is Iron. */}
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 flex items-center gap-1">
                      <span>Material Type</span>
                      {r.material_type && String(r.roof_type || '').trim().toLowerCase() === 'iron' && <span className="text-[8px] px-1 rounded bg-amber-100 text-amber-800 font-semibold">IRON</span>}
                    </div>
                    <div className="text-slate-800 font-medium">{r.material_type || '—'}</div>
                  </div>

                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Pitch</div><div className="text-slate-800 font-medium">{r.roof_pitch || '—'}</div></div>
                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Roof Angle</div><div className="text-slate-800 font-medium font-mono-num">{r.roof_angle ? `${r.roof_angle}°` : '—'}</div></div>
                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Stories</div><div className="text-slate-800 font-medium font-mono-num">{r.roof_stories ?? '—'}</div></div>
                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Colour</div><div className="text-slate-800 font-medium">{r.roof_colour || '—'}</div></div>

                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Surface</div><div className="text-slate-800 font-medium">{r.colour_condition || '—'}</div></div>
                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Growth</div><div className="text-slate-800 font-medium">{r.organic_growth || '—'}</div></div>
                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Age</div><div className="text-slate-800 font-medium">{r.roof_age || '—'}</div></div>
                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">State</div><div className="text-slate-800 font-medium">{r.roof_state || '—'}</div></div>

                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Floor m²</div><div className="text-slate-800 font-medium font-mono-num">{r.floor_m2 || 0}</div></div>
                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Roof m²</div><div className="text-slate-800 font-medium font-mono-num">{r.roof_m2 || 0}</div></div>
                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Ridge length</div><div className="text-slate-800 font-medium font-mono-num">{r.ridge_length || 0} m</div></div>
                  <div><div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Extra Work $</div><div className="text-slate-800 font-medium font-mono-num">${Number(r.extra_work_estimate || 0).toLocaleString()}</div></div>
                </div>

                <div className="mt-3 pt-3 border-t border-[#F0EFEC]">
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1">Extra Work Comments</div>
                  {r.extra_work_comments
                    ? <p className="text-xs text-slate-700 bg-amber-50/60 border border-amber-100 rounded px-2 py-1.5 whitespace-pre-wrap">{r.extra_work_comments}</p>
                    : <p className="text-xs text-slate-400 italic">—</p>
                  }
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Iter206 — old Appraisals block moved into the right-hand
          sidebar above. Kept this comment as a breadcrumb. */}


      {/* Property-level Attachments — tenancy contracts, insurance,
          building reports, supplier docs not tied to a specific
          appraisal. */}
      <div className="mt-6">
        <AttachmentsTab api={api} scope="property" scopeId={id} title="Property Attachments" />
      </div>

      {/* Iter177/178 — universal Soft/Hard Delete modal for the
          property itself and its appraisals. On a successful property
          delete we redirect to the list; for appraisal deletes we
          reload the property. */}
      {delTarget && (
        <DeleteConfirmDialog
          kind={delTarget.kind}
          entityId={delTarget.id}
          label={delTarget.label}
          api={api}
          onClose={() => setDelTarget(null)}
          onDeleted={() => {
            const wasProp = delTarget.kind === 'property';
            setDelTarget(null);
            if (wasProp) navigate('/properties');
            else load();
          }}
        />
      )}
    </div>
  );
}


// RoofForm is now imported from @/components/RoofForm

// Iter208 — Preferred quote-type pill picker. Single-select tags so
// the operator can capture the owner's likely-next-job intent on the
// Property record itself (replacing the dropdown the user vetoed in
// the spec convo). Empty value = no preference.
const PREFERRED_QUOTE_TYPES = [
  { value: 'Reroof',       label: 'Re-roof',     emoji: '🏠' },
  { value: 'New Roof',     label: 'New Roof',    emoji: '🏗️' },
  { value: 'Restoration',  label: 'Restoration', emoji: '✨' },
  { value: 'Repairs',      label: 'Repairs',     emoji: '🔧' },
  { value: 'Maintenance',  label: 'Maintenance', emoji: '🧰' },
  { value: 'Painting',     label: 'Painting',    emoji: '🎨' },
  { value: 'Guttering',    label: 'Guttering',   emoji: '🌧️' },
  { value: 'Treatment',    label: 'Treatment',   emoji: '🧪' },
];

function PreferredQuoteTypePills({ value, onChange }) {
  return (
    <div className="mt-3 space-y-1" data-testid="preferred-quote-type-pills">
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
        Preferred quote type <span className="font-normal normal-case text-slate-400">— pin a category to the top of &ldquo;+ New Appraisal&rdquo; cascade</span>
      </p>
      <div className="flex flex-wrap gap-1">
        {PREFERRED_QUOTE_TYPES.map(t => {
          const active = value === t.value;
          return (
            <button
              key={t.value}
              type="button"
              data-testid={`pqt-pill-${t.value.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => onChange(active ? '' : t.value)}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                active
                  ? 'bg-sky-600 border-sky-600 text-white shadow-sm'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-sky-400 hover:text-sky-700'
              }`}
            >
              {t.emoji} {t.label}
            </button>
          );
        })}
        {value && (
          <button
            type="button"
            data-testid="pqt-pill-clear"
            onClick={() => onChange('')}
            className="text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-700 underline ml-1"
            title="Clear preference"
          >Clear</button>
        )}
      </div>
    </div>
  );
}



// Iter211 — Standalone "Preferred Quote Selection" card. Lives in the
// property's right-hand sidebar above the Appraisals card. The 6 buttons
// match the trades that drive the +New Appraisal quote-type cascade
// (Reroof, New Roof, Remedial, Repairs, Painting, Maintenance). Single
// select — clicking the active pill clears the preference. Writes to
// `properties.preferred_quote_type` via the parent's live-save handler.
// Iter303ag-fix6 — Canonical work-type taxonomy shared with:
//   • Backend /dropdowns/preferred_quote_types (routes/search.py)
//   • Backend quote_categories enum (routes/reports.py :quote_types_enums)
//   • Workflow Runner Action 5 multi-dropdown
// Keep the value strings BYTE-FOR-BYTE identical across all three. Colours
// are property-page-only decoration. Renames from earlier iters:
//   Painting → Re-Coat · Reroof → Re-Roof · Recoat → Re-Coat · Remedial → Remedial Repairs
const PREFERRED_QUOTE_SELECTIONS = [
  { value: 'Re-Roof',          label: 'Re-Roof',          color: 'bg-sky-600 border-sky-600' },
  { value: 'New Roof',         label: 'New Roof',         color: 'bg-indigo-600 border-indigo-600' },
  { value: 'Maintenance',      label: 'Maintenance',      color: 'bg-emerald-600 border-emerald-600' },
  { value: 'Remedial Repairs', label: 'Remedial Repairs', color: 'bg-amber-600 border-amber-600' },
  { value: 'Re-Coat',          label: 'Re-Coat',          color: 'bg-fuchsia-600 border-fuchsia-600' },
  { value: 'Repairs',          label: 'Repairs',          color: 'bg-rose-600 border-rose-600' },
  { value: 'Restoration',      label: 'Restoration',      color: 'bg-purple-600 border-purple-600' },
  { value: 'Guttering',        label: 'Guttering',        color: 'bg-teal-600 border-teal-600' },
];

function PreferredQuoteSelectionCard({ values, onToggle, onClear }) {
  const active = Array.isArray(values) ? values : [];
  return (
    <div
      data-testid="preferred-quote-selection-card"
      className="bg-white border border-[#E2E2DF] rounded-xl p-4 shadow-sm"
    >
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-bold text-[#2C2C2C] flex items-center gap-1.5">
          <span className="text-[#D35400]">★</span> Preferred Quote Selection
        </h2>
        {active.length > 0 && (
          <button
            type="button"
            data-testid="preferred-quote-selection-clear"
            onClick={() => onClear && onClear()}
            className="text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-700 underline"
            title="Clear all preferences"
          >
            Clear
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mb-2.5 leading-snug">
        Reference notes from the intake call — tick any trades the caller mentioned. The <span className="font-semibold text-slate-700">primary</span> appraisal type is picked later in the <span className="font-semibold text-slate-700">Appraisal Booked</span> workflow.
      </p>
      <div className="grid grid-cols-2 gap-1.5">
        {PREFERRED_QUOTE_SELECTIONS.map(opt => {
          const selected = active.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              data-testid={`pqs-btn-${opt.value.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => onToggle && onToggle(opt.value)}
              className={`text-[12px] font-semibold px-2 py-1.5 rounded-md border transition-all ${
                selected
                  ? `${opt.color} text-white shadow-sm`
                  : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400 hover:bg-slate-50'
              }`}
            >
              {selected && '✓ '}{opt.label}
            </button>
          );
        })}
      </div>
      {active.length === 0 && (
        <p className="text-[10px] text-slate-400 mt-2 italic">No preferences set.</p>
      )}
    </div>
  );
}
